<template>
  <hs-modal
    :model-value="true"
    title="Upload Catalog Items"
    kicker="Catalog sync"
    description="Upload an Excel or CSV file to sync products, services, and inventory."
    size="xl"
    body-class="store-sync-modal__body"
    @update:modelValue="handleModalUpdate"
  >
    <div class="store-sync-modal">
      <div class="store-sync-steps" aria-label="Sync progress">
        <div
          v-for="step in steps"
          :key="step.id"
          class="store-sync-step"
          :class="{
            'store-sync-step--active': currentStep === step.id,
            'store-sync-step--complete': currentStep > step.id,
          }"
        >
          <span class="store-sync-step__index">{{ step.id }}</span>
          <span>{{ step.label }}</span>
        </div>
      </div>

      <div v-if="currentStep === 1" class="store-sync-step-panel">
        <div
          class="store-sync-dropzone"
          :class="{ 'store-sync-dropzone--active': isDragOver }"
          @drop="handleFileDrop"
          @dragover.prevent="isDragOver = true"
          @dragenter.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            class="hidden"
            @change="handleFileSelect"
          />
          <div class="store-sync-dropzone__icon">
            <hs-icon name="upload-cloud" :size="28" />
          </div>
          <div>
            <h3>Drop your file here</h3>
            <p>or click anywhere in this area to browse files</p>
          </div>
          <hs-button variant="secondary" icon="folder" @click.stop="$refs.fileInput.click()">
            Choose File
          </hs-button>
          <div class="store-sync-dropzone__hint">
            Excel `.xlsx`, `.xls`, or `.csv` up to 10MB and 1,000 rows
          </div>
        </div>

        <div v-if="selectedFile" class="store-sync-file">
          <div class="store-sync-file__icon">
            <hs-icon name="file-text" :size="18" />
          </div>
          <div class="store-sync-file__body">
            <strong>{{ selectedFile.name }}</strong>
            <span>{{ formatFileSize(selectedFile.size) }}</span>
          </div>
          <hs-button variant="secondary" icon="x" icon-only aria-label="Remove file" @click="removeFile" />
        </div>

        <div class="store-sync-template-panel">
          <div class="store-sync-template-panel__icon">
            <hs-icon name="info" :size="18" />
          </div>
          <div>
            <h4>Need a clean import template?</h4>
            <p>Download the right spreadsheet before uploading catalog items.</p>
            <div class="store-sync-template-panel__actions">
              <hs-button variant="secondary" size="sm" icon="download" @click="downloadTemplate('products')">
                Products Template
              </hs-button>
              <hs-button variant="secondary" size="sm" icon="download" @click="downloadTemplate('services')">
                Services Template
              </hs-button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="currentStep === 2" class="store-sync-processing">
        <div class="store-sync-processing__icon">
          <hs-icon name="loader" :size="30" spin />
        </div>
        <h3>Processing your file</h3>
        <p>Please wait while HovaStore validates and syncs your catalog items.</p>
        <div class="store-sync-progress">
          <div
            class="store-sync-progress__bar"
            :style="{ width: `${progress}%` }"
          ></div>
        </div>
        <span class="store-sync-processing__progress">{{ progress }}% complete</span>
      </div>

      <div v-if="currentStep === 3" class="store-sync-step-panel">
        <SyncTaskResults :result="syncResults" />
      </div>
    </div>

    <template #footer>
      <div class="store-sync-footer">
        <hs-button
          v-if="currentStep > 1"
          variant="secondary"
          icon="arrow-left"
          @click="previousStep"
        >
          Previous
        </hs-button>
        <span v-else></span>
        <div class="store-sync-footer__actions">
          <hs-button variant="secondary" @click="$emit('close')">
            {{ currentStep === 3 ? "Close" : "Cancel" }}
          </hs-button>
          <hs-button
            v-if="currentStep === 1 && selectedFile"
            icon="upload-cloud"
            :disabled="isProcessing"
            :loading="isProcessing"
            @click="startSync"
          >
            Start Sync
          </hs-button>
          <hs-button
            v-if="currentStep === 3"
            icon="refresh-cw"
            @click="resetSync"
          >
            Sync Another File
          </hs-button>
        </div>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import StoreItemSyncApi from "@/helper/storeItemSyncApi.js";
import { toast } from "vue3-toastify";
import SyncTaskResults from "@/components/common/SyncTaskResults.vue";
import { HsButton, HsIcon, HsModal } from "@/components/ui";

export default {
  name: "StoreItemSync",
  props: {
    storeId: {
      type: Number,
      required: true,
    },
  },
  components: { HsButton, HsIcon, HsModal, SyncTaskResults },
  data() {
    return {
      currentStep: 1,
      selectedFile: null,
      isDragOver: false,
      isProcessing: false,
      progress: 0,
      pollingIntervalId: null,
      pollingDelayMs: 1500,
      syncResults: {
        total: 0,
        created: 0,
        updated: 0,
        errors: [],
        headers: [],
        skipped: [],
      },
      steps: [
        { id: 1, label: "Upload" },
        { id: 2, label: "Process" },
        { id: 3, label: "Results" },
      ],
    };
  },
  methods: {
    handleModalUpdate(open) {
      if (!open) {
        this.$emit("close");
      }
    },
    handleFileDrop(e) {
      e.preventDefault();
      this.isDragOver = false;
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.selectFile(files[0]);
      }
    },
    handleFileSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.selectFile(files[0]);
      }
    },
    selectFile(file) {
      // Validate file type
      const allowedTypes = [".xlsx", ".xls", ".csv"];
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();

      if (!allowedTypes.includes(fileExtension)) {
        toast.error("Please select a valid Excel or CSV file");
        return;
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }

      this.selectedFile = file;
    },
    removeFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = "";
    },
    async startSync() {
      if (!this.selectedFile) return;

      this.currentStep = 2;
      this.isProcessing = true;
      this.progress = 0;

      try {
        const response = await StoreItemSyncApi.syncStoreItems(
          this.selectedFile,
          this.storeId,
        );

        // Try to detect async task id
        const respData = response?.data ?? {};
        const payload = respData?.data ?? respData;
        const taskId = payload?.task_id || payload?.taskId || payload?.id;

        if (taskId) {
          // Begin polling for task status
          this.beginPollingTask(taskId);
        } else {
          // Fallback: assume immediate results returned
          this.applySyncResults(payload);
          this.finishSync();
        }
      } catch (error) {
        this.isProcessing = false;
        this.currentStep = 1;

        const errorMessage =
          error.response?.data?.message || "Failed to sync items";
        toast.error(errorMessage);
        console.error("Sync error:", error);
      }
    },
    beginPollingTask(taskId) {
      // Reset progress visual ramp-up to show activity while polling
      this.progress = 10;
      this.clearPolling();
      this.pollingIntervalId = setInterval(async () => {
        try {
          const statusResponse = await StoreItemSyncApi.getTaskStatus(taskId);
          const resp = statusResponse?.data ?? {};
          const data = resp?.data ?? resp;
          const state = data?.state || data?.status || data?.task_state;
          const percent =
            data?.progress ?? data?.percent ?? data?.meta?.progress;

          if (typeof percent === "number") {
            // Keep progress between current and 99 until completion
            this.progress = Math.max(
              this.progress,
              Math.min(99, Math.floor(percent)),
            );
          } else if (this.progress < 95) {
            // Gentle auto-increment if backend doesn't report percent
            this.progress += 1;
          }

          if (
            state === "SUCCESS" ||
            state === "COMPLETED" ||
            state === "DONE"
          ) {
            const result = data?.result ?? data?.payload ?? data;
            this.applySyncResults(result);
            this.finishSync();
          } else if (!state && (data?.result || data?.payload)) {
            // Some backends return only { data: { result: {...} } } without state
            const result = data?.result ?? data?.payload;
            this.applySyncResults(result);
            this.finishSync();
          } else if (state === "FAILURE" || state === "ERROR") {
            const errMsg = data?.error || "Sync task failed";
            this.isProcessing = false;
            this.currentStep = 1;
            this.clearPolling();
            toast.error(errMsg);
          }
        } catch (err) {
          // Stop polling on repeated failures
          this.clearPolling();
          this.isProcessing = false;
          this.currentStep = 1;
          toast.error("Failed to check sync status");
          console.error("Polling error:", err);
        }
      }, this.pollingDelayMs);
    },
    applySyncResults(payload) {
      const safeData = payload || {};
      this.syncResults = {
        total: safeData.total ?? safeData.items_total ?? 0,
        created: safeData.created ?? safeData.items_created ?? 0,
        updated: safeData.updated ?? safeData.items_updated ?? 0,
        errors: Array.isArray(safeData.errors)
          ? safeData.errors
          : Array.isArray(safeData.failures)
          ? safeData.failures
          : [],
        headers: Array.isArray(safeData.headers) ? safeData.headers : [],
        skipped: Array.isArray(safeData.skipped)
          ? safeData.skipped
          : Array.isArray(safeData.skipped_rows)
          ? safeData.skipped_rows
          : [],
      };
    },
    finishSync() {
      this.clearPolling();
      this.progress = 100;
      this.currentStep = 3;
      this.isProcessing = false;
      if (this.syncResults.errors && this.syncResults.errors.length > 0) {
        toast.warning(
          `Sync completed with ${this.syncResults.errors.length} errors`,
        );
      } else {
        toast.success(`Successfully synced ${this.syncResults.total} items`);
      }
      // Notify parent so it can refresh lists
      this.$emit("sync-completed");
    },
    clearPolling() {
      if (this.pollingIntervalId) {
        clearInterval(this.pollingIntervalId);
        this.pollingIntervalId = null;
      }
    },
    async downloadTemplate(type) {
      try {
        const filename =
          type === "products"
            ? "HovaStore Products Template.xlsx"
            : "HovaStore Services Template.xlsx";
        const a = document.createElement("a");
        a.href = `/${encodeURIComponent(filename)}`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success("Template download started");
      } catch (error) {
        toast.error("Failed to trigger template download");
        console.error("Template link error:", error);
      }
    },
    resetSync() {
      this.currentStep = 1;
      this.selectedFile = null;
      this.progress = 0;
      this.syncResults = {
        total: 0,
        created: 0,
        updated: 0,
        errors: [],
        headers: [],
        skipped: [],
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
      this.clearPolling();
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },
    preventDefaults(e) {
      e.preventDefault();
      e.stopPropagation();
    },
  },
  mounted() {
    // Prevent default drag behaviors
    ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      document.addEventListener(eventName, this.preventDefaults, false);
    });
  },
  beforeUnmount() {
    ["dragenter", "dragover", "dragleave", "drop"].forEach((eventName) => {
      document.removeEventListener(eventName, this.preventDefaults, false);
    });
    this.clearPolling();
  },
};
</script>

<style scoped>
.store-sync-modal {
  display: grid;
  gap: 1rem;
}

.store-sync-steps {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 62%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
  display: grid;
  gap: 0.55rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 0.55rem;
}

.store-sync-step {
  align-items: center;
  border-radius: 14px;
  color: var(--hs-muted, #64748b);
  display: inline-flex;
  font-size: 0.8rem;
  font-weight: 950;
  gap: 0.5rem;
  justify-content: center;
  min-height: 2.5rem;
}

.store-sync-step__index {
  align-items: center;
  background: color-mix(in srgb, var(--hs-muted, #64748b) 12%, transparent);
  border-radius: 999px;
  display: inline-flex;
  height: 1.4rem;
  justify-content: center;
  width: 1.4rem;
}

.store-sync-step--active,
.store-sync-step--complete {
  background: var(--hs-surface, #ffffff);
  color: var(--hs-primary, #1d4ed8);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.store-sync-step--complete .store-sync-step__index,
.store-sync-step--active .store-sync-step__index {
  background: color-mix(in srgb, var(--hs-primary, #1d4ed8) 13%, transparent);
}

.store-sync-step-panel {
  display: grid;
  gap: 1rem;
}

.store-sync-dropzone {
  align-items: center;
  background:
    radial-gradient(circle at 88% 0%, color-mix(in srgb, var(--hs-primary, #1d4ed8) 9%, transparent), transparent 34%),
    color-mix(in srgb, var(--hs-surface, #ffffff) 92%, transparent);
  border: 1px dashed color-mix(in srgb, var(--hs-primary, #1d4ed8) 36%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  border-radius: 22px;
  cursor: pointer;
  display: grid;
  gap: 0.9rem;
  justify-items: center;
  min-height: 17rem;
  padding: 2rem;
  text-align: center;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.store-sync-dropzone:hover,
.store-sync-dropzone--active {
  border-color: color-mix(in srgb, var(--hs-primary, #1d4ed8) 62%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  transform: translateY(-1px);
}

.store-sync-dropzone__icon,
.store-sync-processing__icon,
.store-sync-template-panel__icon,
.store-sync-file__icon {
  align-items: center;
  border-radius: 16px;
  display: inline-flex;
  justify-content: center;
}

.store-sync-dropzone__icon,
.store-sync-processing__icon {
  background: color-mix(in srgb, var(--hs-primary, #1d4ed8) 12%, transparent);
  color: var(--hs-primary, #1d4ed8);
  height: 4rem;
  width: 4rem;
}

.store-sync-dropzone h3,
.store-sync-processing h3,
.store-sync-template-panel h4 {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
  margin: 0;
}

.store-sync-dropzone p,
.store-sync-processing p,
.store-sync-template-panel p {
  color: var(--hs-muted, #64748b);
  font-size: 0.88rem;
  font-weight: 750;
  margin: 0.25rem 0 0;
}

.store-sync-dropzone__hint {
  color: var(--hs-muted, #64748b);
  font-size: 0.76rem;
  font-weight: 800;
}

.store-sync-file,
.store-sync-template-panel {
  align-items: center;
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 92%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
  display: flex;
  gap: 0.85rem;
  padding: 0.9rem;
}

.store-sync-file__icon,
.store-sync-template-panel__icon {
  background: color-mix(in srgb, var(--hs-primary, #1d4ed8) 10%, transparent);
  color: var(--hs-primary, #1d4ed8);
  height: 2.5rem;
  width: 2.5rem;
}

.store-sync-file__body {
  display: grid;
  flex: 1;
  min-width: 0;
}

.store-sync-file__body strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.9rem;
  font-weight: 950;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.store-sync-file__body span {
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  font-weight: 800;
}

.store-sync-template-panel {
  align-items: flex-start;
}

.store-sync-template-panel__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.8rem;
}

.store-sync-processing {
  align-items: center;
  display: grid;
  gap: 0.8rem;
  justify-items: center;
  min-height: 20rem;
  text-align: center;
}

.store-sync-progress {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 14%, transparent);
  border-radius: 999px;
  height: 0.62rem;
  margin-top: 0.6rem;
  overflow: hidden;
  width: min(100%, 34rem);
}

.store-sync-progress__bar {
  background: linear-gradient(135deg, var(--hs-primary, #1d4ed8), var(--hs-primary-strong, #1e40af));
  border-radius: inherit;
  height: 100%;
  transition: width 0.3s ease;
}

.store-sync-processing__progress {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 900;
}

.store-sync-footer {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  width: 100%;
}

.store-sync-footer__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  justify-content: flex-end;
}

@media (max-width: 640px) {
  .store-sync-steps {
    grid-template-columns: 1fr;
  }

  .store-sync-dropzone {
    min-height: 14rem;
    padding: 1.25rem;
  }

  .store-sync-file,
  .store-sync-template-panel,
  .store-sync-footer {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
